import * as React from "react";
import { alpha } from "@mui/material/styles";
import CssBaseline from "@mui/material/CssBaseline";
import Box from "@mui/material/Box";
import Container from "@mui/material/Container";
import Typography from "@mui/material/Typography";
import Divider from "@mui/material/Divider";
import Paper from "@mui/material/Paper";
import Stack from "@mui/material/Stack";
import Grid from "@mui/material/Grid2";
import useMediaQuery from '@mui/material/useMediaQuery';
import AppNavbar from "../components/AppNavbar";
import Header from "../components/Header";
import SideMenu from "../components/SideMenu";
import AppTheme from "../../shared-theme/AppTheme";
import {
  chartsCustomizations,
  dataGridCustomizations,
  datePickersCustomizations,
  treeViewCustomizations,
} from "../theme/customizations";

const xThemeComponents = {
  ...chartsCustomizations,
  ...dataGridCustomizations,
  ...datePickersCustomizations,
  ...treeViewCustomizations,
};

function importAll(r) {
  const images = {};
  r.keys().forEach((item) => {
    images[item.replace('./', '')] = r(item);
  });
  return images;
}

const howToUseImages = importAll(require.context('../../howtouse_images', false, /\.(png|jpe?g|svg)$/));

// Placeholder image — a coloured box renders in place of a real screenshot
function ImagePlaceholder({ label, sx }) {
  return (
    <Box
      sx={{
        width: "100%",
        borderRadius: 2,
        bgcolor: (theme) =>
          theme.palette.mode === "dark"
            ? alpha(theme.palette.primary.main, 0.15)
            : alpha(theme.palette.primary.main, 0.08),
        border: "1px solid",
        borderColor: "divider",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        minHeight: 220,
        ...sx,
      }}
    >
      <Typography variant="body2" color="text.secondary">
        {label}
      </Typography>
    </Box>
  );
}

const steps = [
  {
    heading: "Overview",
    body: `IMPR is a tool that allows you to explore image datasets through 2 lenses: similarity-based clustering (Semantic Zoom) and pairwise correlation analysis (Correlations).`,
    imageLabel: "Overview",
    imageRight: false,
    imageFile: "dataset_type_options.png",
  },
  {
    heading: "1. Selecting a dataset and data model",
    body: `IMPR comes backed in with two sample datasets to explore: BSDS300 and CIFAR-10. All their data is stored in two formats: JSON and RDF. This means that the datasets persist
    in two different data models at the same time! You are allowed to query both and export the data you wish in either format!`,
    imageLabel: "Dataset selection",
    imageRight: true,
    imageFile: "dataset_option_selectors.png",
  },
  {
    heading: "2. Explore the visual data",
    body: `The predictions for these datasets were generated by DeepDetect. Check out how the model formed the clusters and predicted the semantic similarities! You might find some
    surprising results!`,
    imageLabel: "Semantic Zoom",
    imageRight: false,
    imageFile: "explore_data_desktop.png",
    mobileImageFile: "explore_data_mobile.png",
  },
  {
    heading: "3. Filtering the data",
    body: `If the data you are seeing is overwhelming, or not enough, use the "Filters" button to filter the data.`,
    imageLabel: "Filter panel",
    imageRight: false,
    imageFile: "filters.png",
  },
  {
    heading: "4. Exporting the data",
    body: `Like aforementioned, if you are interested in extracting a certain portion of the data, you can query the datasets using the existing filters and export the result.`,
    imageLabel: "Export dialog",
    imageRight: true,
    imageFile: "export_dialog.png",
  },
  {
    heading: "5. Creating your own datasets",
    body: `As part of the second major update of IMPR, you are now allowed to form your own datasets! As of the moment of writing, you are only allowed to create datasets in the SQL format.
    Try the "Create" button. Select the dataset type and type the dataset name.`,
    imageLabel: "Create dataset",
    imageRight: true,
    imageFile: "dataset_creation_form.png",
  },
  {
    heading: "6. Creating cluster datasets",
    body: `Creating your own cluster datasets is a little tricky, but fret not! Using the "Add" button, you can create a cluster or an object belonging to a certain cluster manually
    (including the probability, URL) or you can let AI do the work for you! You can have it assign the object to the existing cluster list or let it create a brand new cluster for the
    object.`,
    imageLabel: "Create cluster dataset",
    imageRight: false,
    imageFile: "add_cluster.png",
  },
  {
    heading: "7. SPARQL Playground",
    body: `If, for some reason, you want to practice your SPARQL skills, you can query both default datasets in both dataset types (heatmap and clusters). No DDL/DML operations allowed though!`,
    imageLabel: "SPARQL Playground",
    imageRight: false,
    imageFile: "sparqlplayground.png",
  },
];

export default function HowToUse(props) {
  const isMobile = useMediaQuery("(max-width:900px)");

  return (
    <AppTheme {...props} themeComponents={xThemeComponents}>
      <CssBaseline enableColorScheme />
      <Box sx={{ display: "flex" }}>
        <SideMenu />
        <AppNavbar />
        <Box
          component="main"
          sx={(theme) => ({
            flexGrow: 1,
            backgroundColor: alpha(theme.palette.background.default, 1),
            overflow: "auto",
          })}
        >
          <Stack
            spacing={2}
            sx={{
              alignItems: "center",
              mx: 3,
              pb: 5,
              mt: { xs: 8, md: 0 },
            }}
          >
            <Header />

            <Container maxWidth="lg" sx={{ width: "100%" }}>
              {/* Page title */}
              <Typography variant="h4" fontWeight={700} gutterBottom>
                How to Use IMPR
              </Typography>
              <Typography variant="body1" color="text.secondary" sx={{ mb: 4, maxWidth: 720 }}>
                This is a quick-start guide on how to make the most use of IMPR!
              </Typography>

              <Divider sx={{ mb: 5 }} />

              {/* Steps */}
              <Stack spacing={6}>
                {steps.map((step, idx) => {
                  const imageKey = isMobile && step.mobileImageFile ? step.mobileImageFile : step.imageFile;
                  const imageSrc = imageKey ? howToUseImages[imageKey] : null;

                  return (
                    <Paper
                      key={idx}
                      variant="outlined"
                      sx={{ p: { xs: 2, sm: 3 }, borderRadius: 2 }}
                    >
                      <Grid
                        container
                        spacing={3}
                        alignItems="center"
                        direction={
                          step.imageRight
                            ? { xs: "column", md: "row" }
                            : { xs: "column", md: "row-reverse" }
                        }
                      >
                        {/* Text */}
                        <Grid size={{ xs: 12, md: 6 }}>
                          <Typography
                            variant="h6"
                            fontWeight={600}
                            gutterBottom
                            sx={{ fontSize: { xs: "1rem", md: "1.1rem" } }}
                          >
                            {step.heading}
                          </Typography>
                          <Typography
                            variant="body2"
                            color="text.secondary"
                            sx={{ whiteSpace: "pre-line", lineHeight: 1.8 }}
                          >
                            {step.body}
                          </Typography>
                        </Grid>

                        {/* Image */}
                        <Grid size={{ xs: 12, md: 6 }}>
                          {imageSrc ? (
                            <Box
                              component="img"
                              src={imageSrc}
                              alt={step.imageLabel}
                              sx={{
                                width: "auto",
                                maxWidth: "100%",
                                height: "auto",
                                maxHeight: 420,
                                borderRadius: 2,
                                border: "1px solid",
                                borderColor: "divider",
                                objectFit: "contain",
                                display: "block",
                                marginX: "auto"
                              }}
                            />
                          ) : (
                            <ImagePlaceholder label={step.imageLabel} />
                          )}
                        </Grid>
                      </Grid>
                    </Paper>
                  );
                })}
              </Stack>

              {/* <Divider sx={{ my: 5 }} /> */}

              {/* Tips & tricks gallery — multiple images in a row */}
              {/* <Typography variant="h5" fontWeight={700} gutterBottom>
                Quick-Reference Screenshots
              </Typography> */}
              {/* <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                At a glance — key UI areas labelled for easy reference.
              </Typography> */}
              {/* <Grid container spacing={2}>
                {[
                  "Sidebar navigation",
                  "Filter controls",
                  "Cluster tree",
                  "Heatmap grid",
                  "SPARQL editor",
                  "Export dialog",
                ].map((label, i) => (
                  <Grid key={i} size={{ xs: 12, sm: 6, md: 4 }}>
                    <ImagePlaceholder label={label} sx={{ minHeight: 160 }} />
                    <Typography
                      variant="caption"
                      color="text.secondary"
                      display="block"
                      textAlign="center"
                      mt={0.5}
                    >
                      {label}
                    </Typography>
                  </Grid>
                ))}
              </Grid> */}
            </Container>
          </Stack>
        </Box>
      </Box>
    </AppTheme>
  );
}
